/** 中将棋。Rust chu_shogi.rs の serde 形をそのまま使用する。持ち駒・駒打ちはない。 */
export type Side = 'First' | 'Second';
export type ChuSquare = [number, number];

export type ChuPieceKind =
  | 'Pawn' | 'GoBetween' | 'Copper' | 'Silver' | 'Gold'
  | 'FerociousLeopard' | 'BlindTiger' | 'DrunkElephant' | 'Kirin' | 'Phoenix'
  | 'King' | 'Lance' | 'ReverseChariot' | 'SideMover' | 'VerticalMover'
  | 'Bishop' | 'Rook' | 'DragonHorse' | 'DragonKing' | 'Queen' | 'Lion'
  | 'Tokin' | 'Prince' | 'WhiteHorse' | 'Whale' | 'FreeBoar' | 'FlyingOx'
  | 'FlyingStag' | 'HornedFalcon' | 'SoaringEagle';

export interface ChuPiece {
  kind: ChuPieceKind;
  owner: Side;
  promoted?: boolean;
}

export interface ChuShogiMove {
  from: ChuSquare;
  to: ChuSquare;
  via?: ChuSquare | null;
  promote?: boolean;
}

export type ChuShogiOutcome =
  | { Win: { winner: Side; reason: 'RoyalCaptured' | 'Checkmate' | 'Stalemate' | 'BareKing' | 'Resign' | 'Timeout' } }
  | { Draw: { reason: 'Sennichite' | 'BareKings' | 'MaxPlies' | 'DrawAgreement' } };

export interface ChuShogiState {
  board: (ChuPiece | null)[][];
  side: Side;
  ply: number;
  position_history?: string[];
  move_was_check?: boolean[];
  counter_strike_ban?: boolean;
  bare_king_pending?: Side | null;
  outcome: ChuShogiOutcome | null;
}

export const CHU_SIZE = 12;

/** 盤上は一文字の略字。アクセシブル名・選択パネルには必ず正式名を表示する。 */
export const CHU_PIECES: Record<ChuPieceKind, { kanji: string; name: string }> = {
  Pawn: { kanji: '歩', name: '歩兵' },
  GoBetween: { kanji: '仲', name: '仲人' },
  Copper: { kanji: '銅', name: '銅将' },
  Silver: { kanji: '銀', name: '銀将' },
  Gold: { kanji: '金', name: '金将' },
  FerociousLeopard: { kanji: '豹', name: '猛豹' },
  BlindTiger: { kanji: '虎', name: '盲虎' },
  DrunkElephant: { kanji: '象', name: '酔象' },
  Kirin: { kanji: '麒', name: '麒麟' },
  Phoenix: { kanji: '鳳', name: '鳳凰' },
  King: { kanji: '玉', name: '玉将' },
  Lance: { kanji: '香', name: '香車' },
  ReverseChariot: { kanji: '反', name: '反車' },
  SideMover: { kanji: '横', name: '横行' },
  VerticalMover: { kanji: '竪', name: '竪行' },
  Bishop: { kanji: '角', name: '角行' },
  Rook: { kanji: '飛', name: '飛車' },
  DragonHorse: { kanji: '馬', name: '龍馬' },
  DragonKing: { kanji: '龍', name: '龍王' },
  Queen: { kanji: '奔', name: '奔王' },
  Lion: { kanji: '獅', name: '獅子' },
  Tokin: { kanji: 'と', name: 'と金' },
  Prince: { kanji: '太', name: '太子' },
  WhiteHorse: { kanji: '白', name: '白駒' },
  Whale: { kanji: '鯨', name: '鯨鯢' },
  FreeBoar: { kanji: '猪', name: '奔猪' },
  FlyingOx: { kanji: '牛', name: '飛牛' },
  FlyingStag: { kanji: '鹿', name: '飛鹿' },
  HornedFalcon: { kanji: '鷹', name: '角鷹' },
  SoaringEagle: { kanji: '鷲', name: '飛鷲' },
};

export const CHU_PROMOTES_TO: Partial<Record<ChuPieceKind, ChuPieceKind>> = {
  Pawn: 'Tokin', GoBetween: 'DrunkElephant', Copper: 'SideMover', Silver: 'VerticalMover',
  Gold: 'Rook', FerociousLeopard: 'Bishop', BlindTiger: 'FlyingStag', DrunkElephant: 'Prince',
  Kirin: 'Lion', Phoenix: 'Queen', Lance: 'WhiteHorse', ReverseChariot: 'Whale',
  SideMover: 'FreeBoar', VerticalMover: 'FlyingOx', Bishop: 'DragonHorse', Rook: 'DragonKing',
  DragonHorse: 'HornedFalcon', DragonKing: 'SoaringEagle',
};

export function sameChuSquare(a: ChuSquare | null | undefined, b: ChuSquare | null | undefined): boolean {
  return a == null || b == null ? a == null && b == null : a[0] === b[0] && a[1] === b[1];
}

export function sameChuMove(a: ChuShogiMove, b: ChuShogiMove): boolean {
  return sameChuSquare(a.from, b.from) && sameChuSquare(a.to, b.to)
    && sameChuSquare(a.via, b.via) && Boolean(a.promote) === Boolean(b.promote);
}

export function chuMovesFrom(legal: ChuShogiMove[], from: ChuSquare): ChuShogiMove[] {
  return legal.filter((move) => sameChuSquare(move.from, from));
}

/** direct と via 経由は同じ到着マスでも別の手。経路を省略して送らない。 */
export function chuMovesTo(legal: ChuShogiMove[], from: ChuSquare, to: ChuSquare, via?: ChuSquare | null): ChuShogiMove[] {
  return chuMovesFrom(legal, from).filter((move) => sameChuSquare(move.to, to) && sameChuSquare(move.via, via));
}

export function chuFirstSteps(legal: ChuShogiMove[], from: ChuSquare): ChuSquare[] {
  const seen = new Set<string>();
  return chuMovesFrom(legal, from).flatMap((move) => {
    if (!move.via) return [];
    const key = move.via.join(',');
    if (seen.has(key)) return [];
    seen.add(key);
    return [move.via];
  });
}

/** Potential range with every other piece removed. These hypothetical moves
 * describe the selected piece's current kind/owner, never its live move rights.
 * Actual targets and submission continue to use the real board's legal list.
 */
export function chuPotentialMoves(state: ChuShogiState, from: ChuSquare,
  legalFor: (view: ChuShogiState) => ChuShogiMove[]): ChuShogiMove[] {
  const piece = state.board[from[0]]?.[from[1]];
  if (!piece) return [];
  const board = state.board.map((row) => row.map(() => null as ChuPiece | null));
  board[from[0]][from[1]] = piece;
  const view = { ...state, board, side: piece.owner, outcome: null,
    position_history: [], move_was_check: [], counter_strike_ban: false, bare_king_pending: null };
  return chuMovesFrom(legalFor(view), from);
}

export function chuSquareLabel(square: ChuSquare): string {
  return `${CHU_SIZE - square[1]}筋${square[0] + 1}段`;
}

/** 取って元に戻る＝居喰い、空マス経由で戻る＝じっと。合法手は engine が決める。 */
export function chuMoveName(move: ChuShogiMove, state: ChuShogiState): string {
  if (move.via && sameChuSquare(move.from, move.to)) {
    return state.board[move.via[0]]?.[move.via[1]] ? '居喰い' : 'じっと';
  }
  return move.via ? '二段移動' : '通常・跳躍';
}

export function chuPositionKey(state: ChuShogiState): string {
  return JSON.stringify([state.ply, state.side, state.board, state.counter_strike_ban, state.outcome]);
}
